import React, {
  createContext,
  use,
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react";
import { useTenant, useUserDisplayName } from "hooks/settings";
import {
  useCompleteLoadBasketMutation,
  useUpdateLoadBasketAdditionsMutation,
} from "src/store/api/generatedApi";
import type { LoadBasketRead } from "src/store/api/generatedApi";
import { useAssignedLoads } from "./useAssignedLoads";
import {
  createMaterialKey,
  getGroupedMaterials,
  ADJUSTMENT_AMOUNT,
} from "./utils";
import type {
  MaterialCompletionArgs,
  AdjustmentArgs,
  BasketMaterials,
  GroupedMaterial,
} from "./types";

interface CraneDriverContextValue {
  displayName: string;
  assignedLoads: LoadBasketRead[];
  groupedMaterials: GroupedMaterial[];
  basketMaterials: BasketMaterials;
  submittingMaterialIds: Set<string>;
  submittingBasketId: number | null;
  hasSubmissionError: boolean;
  incrementMass: (args: AdjustmentArgs) => void;
  decrementMass: (args: AdjustmentArgs) => void;
  submitSingleMaterial: (args: MaterialCompletionArgs) => Promise<void>;
  submitBasket: (basketId: number) => Promise<void>;
}

const CraneDriverContext = createContext<CraneDriverContextValue | undefined>(
  undefined
);

export const CraneDriverProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const tenantName = useTenant();
  const displayName = useUserDisplayName();

  const [completeBasket] = useCompleteLoadBasketMutation();
  const [updateBasketAdditions] = useUpdateLoadBasketAdditionsMutation();

  const { data: assignedLoads, refreshBasket } = useAssignedLoads();

  const groupedMaterials = useMemo(
    () => getGroupedMaterials(assignedLoads),
    [assignedLoads]
  );

  const submissionPendingRef = useRef(false);
  const [hasSubmissionError, setHasSubmissionError] = useState(false);
  const [basketMaterials, setBasketMaterials] = useState<BasketMaterials>({});
  const [submittingMaterialIds, setSubmittingMaterialIds] = useState(
    () => new Set<string>()
  );
  const [submittingBasketId, setSubmittingBasketId] = useState<number | null>(
    null
  );

  const incrementMass = useCallback(
    ({ basketId, materialName, layerNumber, targetMass }: AdjustmentArgs) => {
      const key = createMaterialKey(basketId, materialName, layerNumber);
      setBasketMaterials((prev) => {
        const current = prev[key]?.mass ?? targetMass;
        return { ...prev, [key]: { mass: current + ADJUSTMENT_AMOUNT } };
      });
    },
    []
  );

  const decrementMass = useCallback(
    ({ basketId, materialName, layerNumber, targetMass }: AdjustmentArgs) => {
      const key = createMaterialKey(basketId, materialName, layerNumber);
      setBasketMaterials((prev) => {
        const current = prev[key]?.mass ?? targetMass;
        return {
          ...prev,
          [key]: { mass: Math.max(0, current - ADJUSTMENT_AMOUNT) },
        };
      });
    },
    []
  );

  const submitSingleMaterial = useCallback(
    async (args: MaterialCompletionArgs) => {
      if (submissionPendingRef.current) return;
      submissionPendingRef.current = true;
      setHasSubmissionError(false);
      const { basketId, materialId, layerNumber, materialName, currentMass } =
        args;
      const key = createMaterialKey(basketId, materialName, layerNumber);
      setSubmittingMaterialIds((s) => new Set(s).add(key));

      try {
        const updatedBasket = await updateBasketAdditions({
          tenantName,
          loadBasketId: basketId,
          loadBasketAdditionUpdate: {
            materials: [
              {
                material_id: materialId,
                quantity: {
                  value: currentMass,
                  unit: "mass",
                },
                layer_number: layerNumber,
              },
            ],
          },
        }).unwrap();
        await refreshBasket(updatedBasket);
      } catch {
        setHasSubmissionError(true);
      } finally {
        submissionPendingRef.current = false;
        setSubmittingMaterialIds((s) => {
          const next = new Set(s);
          next.delete(key);
          return next;
        });
      }
    },
    [tenantName, updateBasketAdditions, refreshBasket]
  );

  const submitBasket = useCallback(
    async (basketId: number) => {
      if (submissionPendingRef.current) return;
      submissionPendingRef.current = true;
      setHasSubmissionError(false);
      setSubmittingBasketId(basketId);

      try {
        const materials = assignedLoads
          .filter((load) => load.load_basket_id === basketId)
          .flatMap((load) =>
            load.materials
              .filter(
                (material) =>
                  !load.additions.some(
                    (addition) =>
                      addition.material_id === material.material_id &&
                      addition.layer_number === material.layer_number
                  )
              )
              .map((material) => [load.load_basket_id, material] as const)
          )
          .map(([loadBasketId, material]) => {
            const key = createMaterialKey(
              loadBasketId,
              material.material_name,
              material.layer_number
            );
            return {
              material_id: material.material_id,
              quantity: {
                value: basketMaterials[key]?.mass ?? material.quantity.value,
                unit: material.quantity.unit,
              },
              layer_number: material.layer_number,
            };
          });

        const updatedBasket = await completeBasket({
          tenantName,
          loadBasketId: basketId,
          loadBasketAdditionUpdate: { materials },
        }).unwrap();

        await refreshBasket(updatedBasket);
      } catch {
        setHasSubmissionError(true);
      } finally {
        submissionPendingRef.current = false;
        setSubmittingBasketId(null);
      }
    },
    [assignedLoads, basketMaterials, tenantName, completeBasket, refreshBasket]
  );

  const value = useMemo(
    () => ({
      displayName,
      assignedLoads,
      groupedMaterials,
      basketMaterials,
      submittingMaterialIds,
      submittingBasketId,
      hasSubmissionError,
      incrementMass,
      decrementMass,
      submitSingleMaterial,
      submitBasket,
    }),
    [
      displayName,
      assignedLoads,
      groupedMaterials,
      basketMaterials,
      submittingMaterialIds,
      submittingBasketId,
      hasSubmissionError,
      incrementMass,
      decrementMass,
      submitSingleMaterial,
      submitBasket,
    ]
  );

  return <CraneDriverContext value={value}>{children}</CraneDriverContext>;
};

export const useCraneDriver = (): CraneDriverContextValue => {
  const context = use(CraneDriverContext);
  if (!context) {
    throw new Error("useCraneDriver must be used within a CraneDriverProvider");
  }
  return context;
};
